import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { UserService } from '@shared/services/user/user.service';
import { FormAction } from '@shared/interfaces/FormAction';
import { loginSuccess, clearSetupTotp } from '@app/auth/store/auth.actions';
import { Store } from '@ngrx/store';
import { CustomSnackbarService } from '@shared/components/custom-snackbar/custom-snackbar.service';
import { selectRememberDeviceAllowed, selectSetupTotp, selectTotpMfa } from '@app/auth/store/auth.selectors';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import * as QRCode from 'qrcode';

@Component({
    selector: 'app-tfa',
    templateUrl: '../form.html',
    styleUrls: ['../form.scss'],
    standalone: false
})
export class TfaComponent implements OnInit {
  private destroyRef = inject(DestroyRef);

  public form: UntypedFormGroup;
  public exclude: string[];
  public controls: any[];
  public massages: any[];
  public pageTitle: string;
  public formActionBtns: FormAction[];
  public submitted: boolean;
  public numberPatternValidator = Validators.pattern('^[0-9]*$');
  public pageType: string = '2fa';
  public errorText: string = '';
  public rememberDeviceAllowed = true;
  public totpMfa = false;
  public setupTotpCode: string = '';
  public qrCodeData: string = '';
  public qrCodeImageUrl: string = '';

  constructor(private fb: UntypedFormBuilder,
              private router: Router,
              private route: ActivatedRoute,
              private store: Store,
              private userService: UserService,
              private customSnackbarService: CustomSnackbarService) {
  }

  ngOnInit() {
    this.form = this.fb.group({

      tfaCode: this.fb.control('', [Validators.required, this.numberPatternValidator]),
      persist: this.fb.control(false)
    });
    this.store.select(selectRememberDeviceAllowed).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(allowed => {
      this.rememberDeviceAllowed = allowed;
      if (!this.rememberDeviceAllowed) {
        this.form.get('persist').setValue(false);
        this.form.get('persist').disable();
      }
    });

    this.store.select(selectTotpMfa).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(totpMfa => {
      this.totpMfa = totpMfa;
    });

    this.store.select(selectSetupTotp).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(setupTotp => {
      if (setupTotp) {
        this.setupTotp(setupTotp);
      }
    });
    this.pageTitle = 'Verification';
    this.exclude = ['persist'];
    this.massages = [
      {
        title: 'We just sent to your phone a verification code.',
        text: 'Please wait a couple of minutes and if it\'s not received - try to login again in order to request a new verification code.'


      }
    ];
    this.formActionBtns = [
      {
        type: 'a',
        class: 'forgot-pass',
        label: 'Resend verification code',
        action: () => {
          this.userService.resendMfa().subscribe((res) => {
            this.customSnackbarService.showSnackbar('New verification code has been sent. Delivery of SMS/Email might take a couple of minutes', 'success');
          });
        }
      },
      {type: 'button', class: 'tfa-submit-btn', label: 'Continue', action: ''}
    ];
  }

  private setupTotp(setupTotp: string) {
    this.qrCodeData = setupTotp;
    this.setupTotpCode = this.extractSecret(setupTotp);
    this.generateQrCodeImage();
    this.pageTitle = 'Setup Authentication';
    this.pageType = 'setup';
    this.massages = [
      {
        title: 'To keep your account secure, please set up two-factor authentication.',
        text: 'Enter the following code into your authenticator app.'
      }
    ];
    this.formActionBtns = [
      {type: 'button', class: 'tfa-submit-btn', label: 'Confirm', action: ''}
    ];
  }

  private async generateQrCodeImage() {
    try {
      this.qrCodeImageUrl = await QRCode.toDataURL(this.qrCodeData);
    } catch (err) {
      console.error('Failed to generate QR code', err);
      this.qrCodeImageUrl = '';
      this.customSnackbarService.showSnackbar(
        'Failed to generate QR code. Please use the secret code manually.',
        'warning'
      );
    }
  }

  private extractSecret(uri: string): string {
    if (!uri) {
      return '';
    }
    try {
      const url = new URL(uri);
      return url.searchParams.get('secret') || uri;
    } catch (e) {
      // Fallback if not a valid URL
      const match = uri.match(/[?&]secret=([^&]+)/);
      return match ? match[1] : uri;
    }
  }

  send() {
    if (this.form.valid) {
      const remember = this.rememberDeviceAllowed ? this.form.controls['persist'].value : false;
      this.userService.twoFactorsAuthentication(this.form.controls['tfaCode'].value, remember)
        .subscribe(res => {
          const deepCopyData = JSON.parse(JSON.stringify(res));
          this.store.dispatch(loginSuccess({user: deepCopyData }));
          this.store.dispatch(clearSetupTotp());
          if (this.userService.returnUrl) {
            this.router.navigateByUrl(this.userService.returnUrl);
          } else {
            this.router.navigate(['/']);
          }
        }, error => {
          this.form.controls.tfaCode.markAllAsTouched();
          this.form.controls.tfaCode.setErrors({incorrect: true});
        });
    }
  }

}

